import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/lib/seo";

const IMG_GLASS_BUILDING =
    "https://images.unsplash.com/photo-1617761141732-d481912af1a9?crop=entropy&cs=srgb&fm=jpg&q=85&auto=format&fit=crop&w=1400";

const EVOLUTION = [
    { t: "ESPERIENZA", d: "Il punto di partenza: oltre 30 anni di attività al fianco di imprese e professionisti." },
    { t: "NUOVA SOCIETÀ", d: "L'assetto di oggi: una realtà societaria strutturata, moderna e flessibile." },
    { t: "EVOLUZIONE", d: "La direzione di domani: strumenti digitali, gestione da remoto, rapporto agile con il cliente." },
];

export default function ChiSiamo() {
    useSEO(
        "Chi siamo | Service & Business SRLS",
        "Service & Business SRLS: una nuova società costruita su un'esperienza di oltre 30 anni al servizio di imprese e professionisti, con sede a Isernia."
    );

    return (
        <>
            <PageHero
                eyebrow="Chi siamo"
                lines={["Una nuova società, costruita", "su un'esperienza", "di oltre 30 anni."]}
            />

            <section className="pb-8">
                <div className="wrap grid lg:grid-cols-2 gap-14 items-start">
                    <Reveal>
                        <div className="space-y-6">
                            <p className="lead">
                                Service &amp; Business SRLS nasce con l'obiettivo di mettere a disposizione di imprese e
                                professionisti un insieme di servizi contabili, amministrativi e consulenziali
                                organizzati secondo un approccio moderno e flessibile.
                            </p>
                            <p className="text-base text-slate-400 leading-relaxed">
                                L'esperienza maturata in oltre 30 anni rappresenta il punto di partenza. La nuova
                                società guarda invece alle esigenze delle imprese di oggi: maggiore flessibilità,
                                strumenti digitali, gestione da remoto e un rapporto più agile con il cliente.
                            </p>
                            <p className="text-sm text-slate-500 leading-relaxed border-l-2 border-sky-400/40 pl-4">
                                Radicata a Isernia, operativa anche a distanza: lavoriamo con imprese e professionisti
                                del territorio e oltre.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="relative overflow-hidden rounded-3xl border border-white/10">
                            <img
                                src={IMG_GLASS_BUILDING}
                                alt="Architettura contemporanea in vetro, simbolo di solidità e modernità"
                                className="w-full h-72 sm:h-96 object-cover"
                                loading="lazy"
                            />
                            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,15,23,0) 40%, rgba(11,15,23,0.85) 100%)" }} />
                            <p className="absolute bottom-6 left-6 font-mono text-[10px] tracking-[0.28em] text-white uppercase">
                                Solidità — Continuità — Modernità
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="py-20 sm:py-24">
                <div className="wrap">
                    <Reveal>
                        <p className="eyebrow mb-6">Il nostro percorso</p>
                        <h2 className="h2 max-w-3xl">Esperienza, nuova società, evoluzione.</h2>
                    </Reveal>
                    <div className="mt-12 grid md:grid-cols-3 gap-6">
                        {EVOLUTION.map((e, i) => (
                            <Reveal key={e.t} delay={i * 0.08} className="h-full">
                                <div className="card-dark rounded-2xl p-8 h-full relative overflow-hidden group hover:border-sky-400/40 transition-all duration-300" data-testid={`chi-siamo-evoluzione-${i + 1}`}>
                                    <p className="font-display font-extrabold text-6xl text-white/8 leading-none">{String(i + 1).padStart(2, "0")}</p>
                                    <h3 className="font-display text-base font-bold tracking-[0.12em] text-white mt-6">{e.t}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed mt-3">{e.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <div className="mt-10">
                            <Link to="/servizi" data-testid="chi-siamo-cta-servizi" className="btn-ghost">
                                Scopri i nostri servizi
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CTASection
                title="Parliamo della tua attività."
                text="Raccontaci la tua esigenza: mettiamo l'esperienza di oltre 30 anni al servizio dei tuoi obiettivi."
                testid="chi-siamo-final-cta"
            />
        </>
    );
}
